import {
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Box, width } from "@mui/system";
import React, { useState } from "react";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  PageNotFound,
} from "../../../shared";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useOrderDetails } from "../hooks/useOrderDetails";
import { PartialRefundProductTable } from "../components";
import InputControl from "../../../shared/components/InputControl";
import { usePartialRefund } from "../hooks";
import { CustomeDialog } from "../../../shared/components/CustomeDialog";

export const PartialRefund = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isStoreAdmin = pathname.includes("store-admin");
  const { id } = useParams();
  const { state } = useOrderDetails({
    orderId: id,
  });

  const {
    refundState,
    addAll,
    inrementOrDecrementQty,
    onChange,
    onChangeRefundQty,
    onSubmit,
    openRefundModal,
    validator,
    checkIfRefundable,
  } = usePartialRefund({
    orderId: id,
    state,
  });

  if (state.httpStatusCodes.details === 404) {
    return <PageNotFound />;
  }

  return (
    <>
      {state.isDetailsFetching ? (
        <Loader />
      ) : (
        <ContentLayout
          title={`Refund - ${state.order_number}`}
          breadcrumbs={
            <HeaderBreadcrumbs
              links={[
                {
                  name: "Dashboard",
                  href: isStoreAdmin ? "/store-admin" : "/",
                },
                {
                  name: "Sales Log",
                  href: isStoreAdmin ? "/store-admin/orders" : "/orders",
                },
                {
                  name: state.order_number || "",
                  href: isStoreAdmin
                    ? `/store-admin/orders/details/${id}`
                    : `/orders/details/${id}`,
                },
                {
                  name: "Partial Refund",
                },
              ]}
            />
          }
          rightContent={
            <>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => openRefundModal("open")}
              >
                Submit
              </Button>
              <CustomeDialog
                open={refundState.isSubmitOpen}
                handleClose={openRefundModal}
                title={
                  <Typography variant="h6" sx={{ padding: "20px" }}>
                    Refund
                  </Typography>
                }
                content={
                  <Box padding={"20px"}>
                    <Typography>
                      Are you sure you want to continue with the Refund?
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={2}
                      mt={2}
                      justifyContent="flex-end"
                    >
                      <Button variant="outlined" onClick={openRefundModal}>
                        No
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={refundState.isSubmitting}
                        onClick={() => {
                          onSubmit();
                        }}
                      >
                        Yes
                      </Button>
                    </Stack>
                  </Box>
                }
                maxWidth="xs"
              />
            </>
          }
        >
          <Card>
            <Box>
              <Grid
                container
                sx={{
                  height: "83px",
                  padding: "32px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Grid item>
                  <Typography
                    variant="h5"
                    fontSize={16}
                    sx={{
                      lineHeight: "19px",
                    }}
                  >
                    Order Summary
                  </Typography>
                </Grid>
                <Grid item sx={{ display: "flex" }}>
                  <Typography fontWeight={400} mr={1}>
                    Payment Status:
                  </Typography>
                  <Chip
                    label={state.data.payment_status}
                    color="success"
                    variant="filled"
                    size="small"
                    sx={{
                      textTransform: "capitalize",
                    }}
                  />
                </Grid>
              </Grid>
              <Divider />

              <Stack direction="row">
                <Box sx={{ padding: "15px" }}>
                  <Typography
                    fontWeight={700}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                    }}
                  >
                    Store Details
                  </Typography>

                  <div
                    className="text-link"
                    onClick={() =>
                      navigate(`/stores/details/${state?.data?.store_id}`)
                    }
                  >
                    <Typography
                      fontWeight={400}
                      fontSize={12}
                      sx={{
                        lineHeight: "13px",
                        marginBottom: "4px",
                        mt: 1,
                      }}
                    >
                      {state?.data?.store_name}
                    </Typography>
                  </div>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    ABN: {state?.data?.store?.abn ?? ""}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    Address: {state?.data?.store?.address ?? ""}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    Phone: {state?.data?.store?.phone ?? ""}
                  </Typography>
                </Box>

                <Box sx={{ padding: "10px" }}>
                  <Typography
                    fontWeight={700}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                    }}
                  >
                    Bill To
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    {state?.data?.client_account_id ? (
                      <div
                        className="text-link"
                        onClick={() =>
                          navigate(
                            isStoreAdmin
                              ? `/store-admin/client-account-v2/details/${state?.data?.client_account_id}`
                              : `/client-account-v2/details/${state?.data?.client_account_id}`,
                          )
                        }
                      >
                        {state?.data?.client_account?.account_name}
                      </div>
                    ) : state?.data?.customer?.id !== null ? (
                      <div
                        className="text-link"
                        onClick={() =>
                          navigate(
                            isStoreAdmin
                              ? `/store-admin/customers/details/${state?.data?.customer?.id}`
                              : `/customers/details/${state?.data?.customer?.id}`,
                          )
                        }
                      >
                        {state?.data?.customer?.name}
                      </div>
                    ) : (
                      state?.data?.customer?.name
                    )}
                  </Typography>
                  {state?.data?.client_account_id && (
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      ABN: {state?.data?.client_account?.abn_number ?? ""}
                    </Typography>
                  )}
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    {state?.data?.customer?.email ?? ""}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    {state?.data?.customer?.phone_formatted ?? ""}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "6px",
                      mt: 1,
                    }}
                  >
                    {state?.data?.client_account_id
                      ? state?.data?.billing_address
                      : state?.data?.customer_address}
                  </Typography>
                  {state?.data?.loyalty_card_id && (
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Loyalty&nbsp;
                      <span
                        className="text-link"
                        style={{
                          cursor: "pointer",
                          color: "#000",
                          textDecoration: "underline",
                        }}
                        onClick={() =>
                          navigate(
                            `/loyalty-cards/details/${state?.data?.loyalty_card_id}`,
                          )
                        }
                      >
                        #{state?.data?.loyalty_barcode}
                      </span>
                    </Typography>
                  )}
                </Box>

                {state.data.sales_type_id === 103 && (
                  <Box sx={{ padding: "10px" }}>
                    <Typography
                      fontWeight={700}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                      }}
                    >
                      Delivery Address
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      {state?.data?.customer_address ?? ""}
                    </Typography>
                  </Box>
                )}
              </Stack>
              <Divider />
              <Grid
                container
                sx={{
                  padding: "24px 16px",
                }}
              >
                <Grid item xs={3}>
                  <Box sx={{ padding: "10px 0" }}>
                    <Typography
                      fontWeight={700}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                      }}
                    >
                      Order Details
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Order Type: {state.data?.sales_type}{" "}
                      {state.data?.sales_type_id === 103
                        ? `- ${state.data?.delivery_provider}`
                        : ""}
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Cashier:{" "}
                      {state?.data?.finalised_by_user
                        ? state?.data?.finalised_by_user
                        : ""}
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Sales Channel: {state?.data?.channel_name}{" "}
                      {state?.data.channel_id == "101"
                        ? `- ${state?.data.platform ?? ""}`
                        : ""}
                    </Typography>
                    {state?.data?.receipt ? (
                      <>
                        <Typography
                          fontWeight={400}
                          fontSize={13}
                          sx={{
                            lineHeight: "15px",
                            marginBottom: "6px",
                            mt: 1,
                          }}
                        >
                          Web Order No: {state?.data?.receipt}
                        </Typography>
                        {state.data?.channel_id === 104 ? (
                          <Typography
                            fontWeight={400}
                            fontSize={13}
                            sx={{
                              lineHeight: "15px",
                              marginBottom: "6px",
                              mt: 1,
                            }}
                          >
                            Merchant Tip Amount:{" "}
                            {state?.data?.third_party_merchant_tip_amount}
                          </Typography>
                        ) : null}
                      </>
                    ) : null}
                  </Box>
                </Grid>
                <Grid item mt={1} xs={3}>
                  <Stack sx={{ padding: "10px" }}>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Order Number: {state.data?.order_number}
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      Order Date: {state.data?.created_at}
                    </Typography>
                    <Typography
                      fontWeight={400}
                      fontSize={13}
                      sx={{
                        lineHeight: "15px",
                        marginBottom: "6px",
                        mt: 1,
                      }}
                    >
                      {state.data.sales_type_id === 103
                        ? "Delivery Date"
                        : "Pick Up Date"}{" "}
                      : {state.data?.order_date}
                    </Typography>
                  </Stack>
                </Grid>
                {state.data?.po_number !== null ||
                state.data.sales_type_id === 100 ? (
                  <Grid item mt={1} xs={3}>
                    <Stack sx={{ padding: "10px" }}>
                      {state.data?.po_number === null ? (
                        ""
                      ) : (
                        <Typography
                          fontWeight={400}
                          fontSize={13}
                          sx={{
                            lineHeight: "15px",
                            marginBottom: "6px",
                            mt: 1,
                          }}
                        >
                          PO Number: {state.data?.po_number}
                        </Typography>
                      )}
                      {state.data.sales_type_id === 100 && (
                        <>
                          <Typography
                            fontWeight={400}
                            fontSize={13}
                            sx={{
                              lineHeight: "15px",
                              marginBottom: "6px",
                              mt: 1,
                            }}
                          >
                            Table Number: {state.data?.table_number}
                          </Typography>
                          <Typography
                            fontWeight={400}
                            fontSize={13}
                            sx={{
                              lineHeight: "15px",
                              marginBottom: "6px",
                              mt: 1,
                            }}
                          >
                            Cover: {state.data?.table_cover}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Grid>
                ) : null}

                {/*  */}
                {state?.data?.delivery_status ? (
                  <Grid item mt={1} xs={3}>
                    <Stack sx={{ padding: "10px" }}>
                      <Typography
                        fontWeight={400}
                        fontSize={13}
                        sx={{
                          lineHeight: "15px",
                          marginBottom: "6px",
                          mt: 1,
                        }}
                      >
                        Estimated Pickup: {state?.data?.estimated_pick_up_time}
                      </Typography>
                      <Stack
                        sx={{
                          backgroundColor: "#5E736033",
                          padding: "4px 1px",
                          width: "fit-content",
                        }}
                      >
                        <Typography
                          fontWeight={500}
                          fontSize={14}
                          sx={{
                            lineHeight: "17px",
                            marginBottom: "6px",
                            m: 0,
                          }}
                        >
                          Delivery Status: {state?.data?.delivery_status}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
              <PartialRefundProductTable
                state={state}
                refundState={refundState}
                addAll={addAll}
                inrementOrDecrementQty={inrementOrDecrementQty}
                onChange={onChange}
                onChangeRefundQty={onChangeRefundQty}
                checkIfRefundable={checkIfRefundable}
                validator={validator}
              />
              <Divider />
              <Grid
                sx={{
                  padding: "16px 16px 32px",
                }}
                container
              >
                <Grid sx={{ padding: "10px" }} item xs={12}>
                  <Typography
                    fontWeight={500}
                    fontSize={13}
                    sx={{
                      lineHeight: "15px",
                      marginBottom: "10px",
                    }}
                  >
                    Remarks
                  </Typography>
                  <InputControl
                    type="textarea"
                    label="Reason*"
                    name="reason"
                    placeholder="Describe..."
                    error={validator.current.message(
                      "Reason",
                      refundState.reason,
                      "required",
                    )}
                    helperText={validator.current.message(
                      "Reason",
                      refundState.reason,
                      "required",
                    )}
                    value={refundState.reason}
                    onChange={onChange}
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>
        </ContentLayout>
      )}
    </>
  );
};
